"use client";

import { PauseIcon, PlayIcon } from "@heroicons/react/24/solid";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/buttons/Button";
import { Spinner } from "@/components/ui/Spinner";
import { useAudioPlaying } from "@/lib/zustand/useAudioPlaying";
import type { AudioPreviewRegistration } from "@/types/defs/audioPreviewRegistration";
import { cn } from "@/utils/cn";
import { logError } from "@/utils/logError";

export function SmallAudioPlayer({
  source,
  className,
  blackIcons,
  name = "audio",
  isEnabled = true,
  registerPlayback,
}: {
  source: string;
  blackIcons: boolean;
  className?: string;
  name?: string;
  isEnabled?: boolean;
  registerPlayback?: (pause: () => void) => AudioPreviewRegistration;
}) {
  const [status, setStatus] = useState<"paused" | "loading" | "playing">(
    "paused"
  );
  const player = useRef<{ toggle: () => void } | null>(null);

  useEffect(() => {
    setStatus("paused");
    if (!isEnabled) {
      return;
    }
    const audio = new Audio();
    audio.preload = "none";
    audio.src = source;
    let isDisposed = false;
    let isPlayWanted = false;
    let generation = 0;
    const releaseGlobalPlayer = () => {
      const shared = useAudioPlaying.getState();
      if (shared.audioPlaying === audio) {
        shared.setAudioPlaying(null);
      }
    };
    const pause = () => {
      isPlayWanted = false;
      generation += 1;
      audio.pause();
      audio.currentTime = 0;
      releaseGlobalPlayer();
      if (!isDisposed) {
        setStatus("paused");
      }
    };
    const registration = registerPlayback?.(pause);
    const fail = (error: unknown) => {
      if (isDisposed || !isPlayWanted) {
        return;
      }
      pause();
      logError(error, { showToast: false });
      logError(
        new Error("This preview could not be played. Please try again.")
      );
    };
    audio.onplaying = () => {
      if (isDisposed || !isPlayWanted) {
        audio.pause();
        return;
      }
      setStatus("playing");
    };
    audio.onwaiting = () => {
      if (!isDisposed && isPlayWanted) {
        setStatus("loading");
      }
    };
    audio.onpause = () => {
      if (!audio.paused) {
        return;
      }
      isPlayWanted = false;
      generation += 1;
      releaseGlobalPlayer();
      if (!isDisposed) {
        setStatus("paused");
      }
    };
    audio.onended = pause;
    audio.onerror = () =>
      fail(audio.error ?? new Error("Preview media failed"));
    const unsubscribe = useAudioPlaying.subscribe((state) => {
      if (state.audioPlaying !== audio && isPlayWanted) {
        pause();
      }
    });
    const play = async () => {
      if (registration?.requestPlay() === false) {
        return;
      }
      const shared = useAudioPlaying.getState();
      if (shared.audioPlaying !== audio && shared.audioPlaying !== null) {
        shared.pauseAudio();
        shared.setAudioPlayingToZero();
      }
      isPlayWanted = true;
      const attempt = ++generation;
      shared.setAudioPlaying(audio);
      setStatus("loading");
      try {
        await audio.play();
        // A retired request may finish after another preview started. It cannot reclaim playback or pause a newer request on this same element.
        if (isDisposed || !isPlayWanted) {
          audio.pause();
        }
      } catch (error) {
        if (attempt === generation) {
          fail(error);
        }
      }
    };
    const controls = {
      toggle: () => {
        if (isPlayWanted) {
          pause();
        } else {
          void play();
        }
      },
    };
    player.current = controls;
    return () => {
      isDisposed = true;
      unsubscribe();
      registration?.dispose();
      pause();
      audio.onplaying = null;
      audio.onwaiting = null;
      audio.onpause = null;
      audio.onended = null;
      audio.onerror = null;
      audio.removeAttribute("src");
      audio.load();
      if (player.current === controls) {
        player.current = null;
      }
    };
  }, [source, isEnabled, registerPlayback]);

  const isRequested = status !== "paused";
  const Icon = isRequested ? PauseIcon : PlayIcon;
  return (
    <Button
      type="button"
      variant="ghost"
      disabled={!isEnabled}
      aria-label={`${isRequested ? "Pause" : "Play"} preview of "${name}"`}
      aria-busy={status === "loading"}
      onClick={(event) => {
        event.stopPropagation();
        player.current?.toggle();
      }}
      className={cn(
        "p-1 transition-all hover:scale-110 hover:opacity-80",
        className
      )}
    >
      {status === "loading" ?
        <Spinner />
      : <Icon
          className={cn(
            "h-7 w-7 shrink-0",
            blackIcons ? "text-gray-900" : "text-white"
          )}
        />
      }
    </Button>
  );
}
